<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div style="position: relative">
    <div
      v-if="showTitle"
      class="d-flex "
    >
      <div
        class="py-4 d-flex align-center"
        style="width:100%"
      >
        <!-- Navigation slot: Used for navigation elements -->
        <slot name="navigation" />
        <span
          class="text-h3 font-weight-bold text-truncate"
        >
          {{ $t(entityName) || model.name() }}
        </span>
        <v-spacer />
        <div
          class="align-center"
          style="display: grid;grid-auto-flow: column;grid-gap: 8px"
        >
          <!-- Footer slot: Used for displaying information -->
          <slot
            :date-time="dates"
            :items="items"
            :select-items="selectedItems"
            :table-items="tableItem"
            name="footer"
          />
        </div>
      </div>
    </div>
    <template>
      <div
        class="d-flex filterBar align-center mb-4"
      >
        <horizontal-list>
          <v-btn
            v-if="filterableField.length>0"
            style="background: white"
            elevation="0"
            @click="showFilter=true"
          >
            <v-icon>mdi-filter-outline</v-icon>
            过滤
          </v-btn>
          <template
            v-if="filterDisplayChips.length>0"
          >
            <v-card
              elevation="0"
              v-for="item in filterDisplayChips"
              :key="item.key"
              color="blue darken-4 white--text"
              class="mr-2 pa-2 text-body-2 d-flex align-center justify-center text-no-wrap text-truncate filter-chip"
              close
              dark
              @click="removeFilter(item)"
            >
              <span class="mr-1">
                {{ item.name }}:
              </span>
              {{ item.value }}
              <v-icon right>
                mdi-close-circle
              </v-icon>
            </v-card>
          </template>
        </horizontal-list>

        <batch-operations
          :show-batch="showBatch"
          :selected-items.sync="selectedItems"
          :editable-fields="editableFields"
          :addable-fields="addableFields"
          :can-mass-edit="canMassEdit"
          :model="model"
          :use-u-uid="useUUid"
          @reload="reload"
        />
        <template v-if="useDateFilter">
          <div
            class="d-flex align-center"
            style="max-width: 300px; height: 54px;"
          >
            <v-btn
              style="background: white"
              elevation="0"
              outlined
              @click="datePickerMenu=true"
            >
              <v-icon left>
                mdi-calendar
              </v-icon>
              <template>
                {{ getNiceLabel(dates) }}
              </template>
            </v-btn>
          </div>
        </template>
        <v-spacer />

        <div class="d-flex ma-0 ml-2 align-center">
          <div
            class="d-flex align-center mr-2 flex-grow-1 flex-shrink-1"
            style="height: 100%;"
          >
            <v-text-field
              v-model="search"
              :label="$t('search')"
              class="white"
              clearable
              dense
              hide-details
              outlined
              prepend-inner-icon="mdi-magnify"
              single-line
              style="min-width: 150px"
            />
          </div>
        </div>
        <!-- PrimaryButton slot: Used for the main action button (default is Add) -->
        <horizontal-list>
          <!-- Right slot: Used for additional operation buttons -->
          <slot
            :date-time="dates"
            :items="items"
            :select-items="selectedItems"
            :table-items="tableItem"
            name="right"
          />
          <slot name="primaryButton">
            <v-btn
              v-if="useDefaultAction && useAddAction&&!onlyDisplay"
              :class="entityName + '-add-button'"
              color="amber lighten-4 black--text"
              elevation="0"
              @click="addItem"
            >
              <div
                class="d-flex align-center"
              >
                <v-icon
                  left
                >
                  mdi-plus-circle-outline
                </v-icon>
                {{ $t(entityName) }}
              </div>
            </v-btn>
          </slot>
        </horizontal-list>
      </div>
    </template>

    <div
      class="d-flex filterBar align-center"
    >
      <!-- TopOnTable slot: Used for content displayed at the top of the table -->
      <slot
        :date-time="dates"
        :items="items"
        :select-items="selectedItems"
        :table-items="tableItem"
        name="topOnTable"
      />
    </div>

    <template v-if="realCategoryList.length>0">
      <v-tabs
        v-model="activeCategoryFilterIndex"
        show-arrows
        style="background: transparent;"
      >
        <v-tab
          v-for="c in realCategoryList"
          :key="c.id"
        >
          {{ c.name }}
        </v-tab>
      </v-tabs>
      <v-divider />
    </template>

    <v-card elevation="0">
      <v-data-table
        v-model="selectedItems"
        class=""
        :footer-props="{itemsPerPageOptions:[5,10,30,50,-1]}"
        :headers="realHeaders"
        :item-class="getItemClass"
        :item-key="itemKey"
        :items="tableItem"
        :items-per-page="itemPerPage"
        :loading="loading"
        :mobile-breakpoint="20"
        :search.sync="search"
        :show-select="canMassEdit"
        @update:page="pageUpdate"
        @click:row.self="editItem"
      >
        <!-- Add custom footer with config button and export button -->
        <template #footer.prepend>
          <v-btn
            small
            text
            color="primary"
            class="mr-2"
            @click="openHeaderConfig"
          >
            <v-icon
              small
              class="mr-1"
            >
              mdi-cog
            </v-icon>
            {{ $t('config') }}
          </v-btn>
          <excel-export
            v-if="tableItem.length > 0"
            :headers="realHeaders"
            :items="tableItem"
            :file-name="entityName || model.name()"
          />
        </template>
        <template #item.action="{item}">
          <v-menu
            close-on-content-click
            left
            offset-overflow
            offset-y
            rounded
          >
            <template #activator="{on,attrs}">
              <v-btn
                class="ma-auto mr-0 pa-0"
                height="40px"
                icon
                large
                style="border-radius: 12px"
                v-bind="attrs"
                width="40px"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list
              outlined
              width="200px"
            >
              <v-list-item-group>
                <v-list-item
                  v-if="useEditAction"
                  @click="editItem(item)"
                >
                  <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="useDeleteAction"
                  @click="deleteItem(item)"
                >
                  <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
                </v-list-item>
                <slot
                  :item="item"
                  name="item.action"
                />
              </v-list-item-group>
            </v-list>
          </v-menu>
        </template>
        <template
          v-for="slottedItem in slottedItems"
          #[slottedItem.name]="{ item }"
        >
          <slot
            :item="item"
            :name="slottedItem.name"
          />
        </template>
        <template
          v-for="adItem in advancedItems"
          #[adItem.name]="{ item }"
        >
          <template
            v-if="
              adItem.dataType===Types.Image"
          >
            <img-template
              :key="adItem.name"
              :item="item"
              :model="adItem.value"
              :root="adItem.type.root()"
            />
          </template>
          <template
            v-else-if="adItem.dataType===Types.Group"
          >
            <div
              v-for="(c) in adItem.childKey.filter(adItem.displayChild)"
              :key="'_'+adItem.value+c"
            >
              {{ item['_' + adItem.value + c] }}
            </div>
          </template>

          <template
            v-else-if="
              adItem.dataType===Types.Boolean"
          >
            <div
              :key="adItem.name"
              style="width: min-content"
            >
              <v-simple-checkbox
                :disabled="loading"
                :value="!!item[adItem.value]"
                color="primary"
                dense
                @click="toggleProperty(item,adItem.value)"
              />
            </div>
          </template>

          <template
            v-else-if="
              adItem.dataType===Types.Color"
          >
            <v-chip
              :key="adItem.name"
              :color="item[adItem.value]"
              :style="swatchStyle"
              label
            />
          </template>
          <template
            v-else-if="
              adItem.dataType===Types.Option"
          >
            <div
              :key="adItem.name"
              class="d-flex"
            >
              <template v-for="(value,i) in [item[adItem.value]].flat().splice(0,2)">
                <div :key="value+'.'+i">
                  <template v-if="adItem.type.color">
                    <span
                      :class="
                        adItem.type.color
                          .find(c=>{return parseInt(value)===c.id})?.color ?? 'grey'"
                      class="pa-1 px-3 mr-1 white--text rounded-pill text-no-wrap"
                    >
                      {{ [item['opt' + adItem.value]].flat()[i] }}
                    </span>
                  </template>
                  <template v-else>
                    <span> {{ [item['opt' + adItem.value]].flat()[i] }}</span>
                  </template>
                </div>
              </template>
              <template v-if="[item[adItem.value]].flat().length>2">
                (+{{ [item[adItem.value]].flat().length - 2 }})
              </template>
            </div>
          </template>
          <template
            v-else-if="
              adItem.dataType===Types.Date"
          >
            <div :key="adItem.name">
              {{ item[adItem.value] | beautifulTime }}
            </div>
          </template>
          <template
            v-else-if="
              adItem.dataType===Types.Float"
          >
            <price-table-display
              :key="adItem.name"
              :price="item[adItem.value]"
            />
          </template>
        </template>
        <template #no-data>
          <!-- No-data slot: Used to display content when there's no data in the table -->
          <slot name="no-data">
            <div
              class="d-flex flex-column justify-center align-center"
              style="width: 100%;height: 400px"
            >
              <v-icon
                color="#eee"
                size="100"
              >
                mdi-file-document-alert-outline
              </v-icon>
              <div class="mt-4 text-body-1">
                {{ $t('CurrentlyNoData') }}
              </div>
              <v-btn
                class="mt-4"
                color="amber lighten-4 black--text"
                elevation="0"
                @click="reload"
              >
                {{ $t('reload') }}
              </v-btn>
            </div>
          </slot>
        </template>

        <template #expanded-item="{ item }">
          <td :colspan="headers.length">
            <!-- Expanded-item slot: Used to display additional content when a row is expanded -->
            <slot
              :item="item"
              name="expanded-item"
            />
          </td>
        </template>
      </v-data-table>
    </v-card>
    <!-- BaseFormDialog is used instead of IkEntityForm, managed through baseFormDialogStore -->
    <!-- Header Configuration Dialog -->
    <v-dialog
      v-model="headerConfigDialog"
      max-width="600px"
      scrollable
    >
      <v-card>
        <div class="headline pa-3 grey lighten-2">
          {{ $t('TableHeaderConfiguration') }}
        </div>

        <v-card-text class="pt-4">
          <p class="mb-4">
            {{ $t('ConfigureTableHeadersDescription') }}
          </p>

          <draggable
            v-model="configHeaders"
            handle=".drag-handle"
            @change="updateHeadersToIndex"
            :disabled="false"
            :options="{
              preventOnFilter: true,
              filter: '.action-header'
            }"
          >
            <v-card
              v-for="(item) in configHeaders"
              :key="item.value"
              class="mb-2 pa-2"
              outlined
              :class="{'action-header': item.value === 'action'}"
            >
              <div class="d-flex align-center">
                <v-icon
                  v-if="item.value !== 'action'"
                  class="drag-handle mr-2 grey--text"
                  small
                >
                  mdi-drag
                </v-icon>
                <v-icon
                  v-else
                  class="mr-2 amber--text"
                  small
                >
                  mdi-lock
                </v-icon>
                <div class="flex-grow-1">
                  {{ item.text }}
                </div>
                <v-checkbox
                  v-model="item.visible"
                  :disabled="item.value === 'action'"
                  hide-details
                  dense
                  class="ml-2"
                />
              </div>
            </v-card>
          </draggable>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            color="warning"
            text
            @click="resetHeaderConfig"
          >
            {{ $t('ResetToDefault') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="headerConfigDialog = false"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="saveHeaderConfig"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="datePickerMenu"
      max-width="400px"
    >
      <v-card
        class="pa-2"
        color="#efefef"
        @click="datePickerMenu=false"
      >
        <date-range-picker v-model="dates" />
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showFilter"
      max-width="700px"
      scrollable
    >
      <v-card>
        <div class="pa-2 grey lighten-4">
          <span class="text-h5 font-weight-black">{{ $t('FilterOptions') }}</span>
        </div>
        <div
          class="py-3 px-3"
          style="max-height: 65vh;overflow-y: scroll"
        >
          <v-row dense>
            <template v-for="(field) in filterableField">
              <v-col
                :key="field.value"
                cols="12"
                sm="6"
                class="pa-1"
              >
                <div class="filter-field-container">
                  <template v-if="field.dataType === Types.Float || field.dataType === Types.Number">
                    <div class="filter-field-title">
                      {{ field.text }}
                    </div>
                    <v-row dense>
                      <v-col
                        cols="12"
                        class="pa-1"
                      >
                        <v-text-field
                          v-model="filterRanges[field.value].min"
                          :label="$t('Min') + ' ' + field.text"
                          outlined
                          hide-details
                          type="number"
                          class="mb-1"
                          dense
                          prepend-inner-icon="mdi-arrow-down"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        class="pa-1"
                      >
                        <v-text-field
                          v-model="filterRanges[field.value].max"
                          :label="$t('Max') + ' ' + field.text"
                          outlined
                          hide-details
                          type="number"
                          class="mb-3"
                          dense
                          prepend-inner-icon="mdi-arrow-up"
                        />
                      </v-col>
                    </v-row>
                  </template>
                  <template v-else-if="field.dataType === Types.Date">
                    <div class="filter-field-title">
                      {{ field.text }}
                    </div>
                    <v-row dense>
                      <v-col
                        cols="12"
                        class="pa-1"
                      >
                        <v-menu
                          v-model="dateMenus[field.value].start"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template #activator="{ on, attrs }">
                            <v-text-field
                              v-model="filterDateRanges[field.value].start"
                              :label="$t('StartDate')"
                              readonly
                              outlined
                              hide-details="auto"
                              v-bind="attrs"
                              v-on="on"
                              prepend-inner-icon="mdi-calendar"
                              class="mb-1"
                              dense
                            />
                          </template>
                          <v-date-picker
                            v-model="filterDateRanges[field.value].start"
                            @input="dateMenus[field.value].start = false"
                            width="290"
                          />
                        </v-menu>
                      </v-col>
                      <v-col
                        cols="12"
                        class="pa-1"
                      >
                        <v-menu
                          v-model="dateMenus[field.value].end"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template #activator="{ on, attrs }">
                            <v-text-field
                              v-model="filterDateRanges[field.value].end"
                              :label="$t('EndDate')"
                              readonly
                              outlined
                              hide-details="auto"
                              v-bind="attrs"
                              v-on="on"
                              prepend-inner-icon="mdi-calendar"
                              class="mb-1"
                              dense
                            />
                          </template>
                          <v-date-picker
                            v-model="filterDateRanges[field.value].end"
                            @input="dateMenus[field.value].end = false"
                            width="290"
                          />
                        </v-menu>
                      </v-col>
                    </v-row>
                  </template>
                  <template v-else-if="field.dataType === Types.String">
                    <div class="filter-field-title">
                      {{ field.text }}
                    </div>
                    <v-row dense>
                      <v-col
                        cols="12"
                        class="pa-1"
                      >
                        <v-text-field
                          v-model="filterItem[field.value]"
                          :label="field.text"
                          outlined
                          hide-details="auto"
                          class="mb-1"
                          dense
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        class="pa-1"
                      >
                        <v-select
                          v-model="textFilterOperations[field.value]"
                          :items="textOperations"
                          item-text="text"
                          item-value="value"
                          outlined
                          hide-details="auto"
                          class="mb-1"
                          dense
                        />
                      </v-col>
                    </v-row>
                  </template>
                  <template v-else>
                    <div class="filter-field-title">
                      {{ field.text }}
                    </div>
                    <form-field
                      :edited-item="filterItem"
                      :field="field"
                      :hide-select="true"
                      outlined
                      class="mb-1"
                      dense
                    />
                  </template>
                </div>
              </v-col>
            </template>
          </v-row>
        </div>

        <v-divider />

        <div class="pa-3 d-flex">
          <v-btn
            color="error"
            text
            large
            class="px-4"
            @click="clearFilters"
          >
            <v-icon left>
              mdi-filter-remove
            </v-icon>
            {{ $t('ClearFilters') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            elevation="2"
            large
            class="px-6"
            @click="applyFilters"
          >
            <v-icon left>
              mdi-check
            </v-icon>
            {{ $t('ApplyFilters') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'
import { convertToBaseFormSchema } from '@/model/baseForm/dataEntityFormUtils'
import ImgTemplate from './Base/ImgTemplate.vue'
import FormField from './FormField.vue'
import IKUtils from 'innerken-js-utils'
import { groupBy } from 'lodash'
import { Ripple } from 'vuetify/lib/directives'
import { intersection, keyBy, sortBy, uniqBy } from 'lodash-es'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'
import DateRangePicker from '@/views/widget/commonLogic/DateRangePicker.vue'
import PriceTableDisplay from '@/views/widget/typography/PriceTableDisplay.vue'
import { getNiceLabel } from '@/model/utils/DateRepository'
import dayjs from 'dayjs'
import BatchOperations from './BatchOperations.vue'
import HorizontalList from '@/views/widget/uiComponent/HorizontalList.vue'
import draggable from 'vuedraggable'
import ExcelExport from './ExcelExport.vue'

export default {
  name: 'IkDataTable',
  components: {
    HorizontalList,
    PriceTableDisplay,
    DateRangePicker,
    ImgTemplate,
    FormField,
    BatchOperations,
    draggable,
    ExcelExport
  },
  directives: {
    Ripple
  },
  props: {
    showBatch: {
      default: true
    },
    itemKey: {
      default: 'id'
    },
    needLoading: {
      default: false
    },
    itemPerPage: {
      default: 30
    },
    useUUid: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      default: () => {
      }
    },
    addText: {
      type: String,
      default: 'Add'
    },
    entityName: {
      type: String,
      default: ''
    },
    filter: {
      type: [Object, Function],
      default: () => {
      }
    },
    icon: {
      type: String,
      default: ''
    },
    showExpand: {
      type: Boolean,
      default: false
    },
    singleExpand: {
      type: Boolean,
      default: false
    },
    useSelect: {
      type: Boolean,
      default: true
    },
    useAddFilter: {
      type: Boolean,
      default: true
    },
    useDateFilter: {
      type: Boolean,
      default: false
    },
    useDefaultAction: {
      type: Boolean,
      default: true
    },
    useEditAction: {
      type: Boolean,
      default: true
    },
    useDeleteAction: {
      type: Boolean,
      default: true
    },
    useCustomAction: {
      type: Boolean,
      default: false
    },
    useAddAction: {
      type: Boolean,
      default: true
    },
    requiredDateValue: {},
    hideSelectedAction: {
      type: Boolean,
      default: false
    },
    customOnRowClick: {},
    fixedFilter: {},
    showTitle: {
      default: true
    },
    categoryList: {},
    categoryFilterFunc: {},
    onlyDisplay: {}

  },
  data: function () {
    return {
      page: 1,
      filterItem: {},
      Types: IKDataEntity.Types,

      search: '',

      loading: false,
      items: [],
      headers: [],
      formField: [],
      editedIndex: -1,
      editedItem: null,
      defaultItem: null,
      advancedItems: [],
      slottedItems: [],
      datePickerMenu: false,
      dates: null,
      formDisc: {},
      showFilter: false,
      activeCategoryFilterIndex: '',

      lastClickItemId: null,
      selectedItems: [],

      // Header configuration dialog
      headerConfigDialog: false,
      configHeaders: [],
      headerConfigUpdated: 0, // Counter to track header configuration updates

      // New filter-related properties
      filterRanges: {},
      filterDateRanges: {},
      dateMenus: {},
      textFilterOperations: {},
      textOperations: [
        { text: this.$t('Contains'), value: 'contains' },
        { text: this.$t('StartsWith'), value: 'startsWith' },
        { text: this.$t('EndsWith'), value: 'endsWith' },
        { text: this.$t('Equals'), value: 'equals' }
      ]
    }
  },
  computed: {
    okDates () {
      return this.dates
    },
    realFilter () {
      const res = this.filter ?? {}
      if (this.okDates) {
        res.dateFilter = this.okDates
      }
      return res
    },
    swatchStyle () {
      return {
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderStyle: 'solid',
        borderColor: '#c1c1c1',
        borderWidth: '1px',
        transition: 'border-radius 200ms ease-in-out'
      }
    },
    editableFields: function () {
      return uniqBy([this.formField, this.headers].flat(), 'value')
        .filter(item =>
          [IKDataEntity.Types.Boolean, IKDataEntity.Types.Option].includes(item.dataType))
        .filter(item => item.editable)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },
    filterableField: function () {
      return uniqBy([this.formField, this.headers].flat(), 'value')
        .filter(item =>
          [IKDataEntity.Types.Boolean, IKDataEntity.Types.Option, IKDataEntity.Types.String, IKDataEntity.Types.Integer, IKDataEntity.Types.Float].includes(item.dataType) || item.type?.needFilter)
        .filter(item => item.merge)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },
    addableFields: function () {
      return this.formField
        .filter(item => [IKDataEntity.Types.Boolean, IKDataEntity.Types.Option].includes(item.dataType))
        .filter(item => {
          return item.merge && item.type?.multiple
        })
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },
    canMassEdit: function () {
      return this.editableFields.length > 0 && this.useDefaultAction && !this.onlyDisplay
    },
    tableItem: function () {
      let target = this.items

      // Apply standard filters from filterItem
      if (this.filterItem && !this.showFilter) {
        target = target.filter(i => {
          return Object.keys(this.filterItem).filter(k => this.filterItem[k] != null).every(
            t => {
              // Skip if the item doesn't have this property
              if (i[t] === undefined || i[t] === null) return false

              const field = this.formDisc[t]

              // Handle string fields with text operations
              if (field?.dataType === IKDataEntity.Types.String) {
                const itemValue = String(i[t]).toLowerCase()
                const filterValue = String(this.filterItem[t]).toLowerCase()
                const operation = this.textFilterOperations[t]

                switch (operation) {
                  case 'contains':
                    return itemValue.includes(filterValue)
                  case 'startsWith':
                    return itemValue.startsWith(filterValue)
                  case 'endsWith':
                    return itemValue.endsWith(filterValue)
                  case 'equals':
                    return itemValue === filterValue
                  default:
                    return itemValue.includes(filterValue)
                }
              }

              // Handle regular fields (boolean, option, etc.)
              const org = i[t].toString()
              const oth = this.filterItem[t].toString()
              const operation = this.filterableField.find(it => it.value === t)?.type?.operation
              let result = []
              let stringOrg = []
              let stringOth = []
              if (Array.isArray(org)) {
                stringOrg = org.map(x => x.toString())
                stringOth = oth.map(x => x.toString())
                result = intersection(stringOrg, stringOth)
              }
              return this.compareFunction(org, oth, operation) || result.length > 0 || (Array.isArray(org) && (org.includes(oth) || (Array.isArray(
                oth) && oth.every(
                ot => org.includes(ot)))))
            })
        })
      }

      // Apply numeric range filters
      for (const fieldKey in this.filterRanges) {
        const range = this.filterRanges[fieldKey]
        if ((range.min !== null && range.min !== '') || (range.max !== null && range.max !== '')) {
          target = target.filter(item => {
            const value = parseFloat(item[fieldKey])
            if (isNaN(value)) return false

            const min = range.min !== null && range.min !== '' ? parseFloat(range.min) : Number.NEGATIVE_INFINITY
            const max = range.max !== null && range.max !== '' ? parseFloat(range.max) : Number.POSITIVE_INFINITY

            return value >= min && value <= max
          })
        }
      }

      // Apply date range filters
      for (const fieldKey in this.filterDateRanges) {
        const dateRange = this.filterDateRanges[fieldKey]
        if (dateRange.start || dateRange.end) {
          target = target.filter(item => {
            if (!item[fieldKey]) return false

            const itemDate = dayjs(item[fieldKey])
            let isValid = true

            if (dateRange.start) {
              const startDate = dayjs(dateRange.start)
              isValid = (isValid && itemDate.isAfter(startDate)) || itemDate.isSame(startDate, 'day')
            }

            if (dateRange.end) {
              const endDate = dayjs(dateRange.end)
              isValid = (isValid && itemDate.isBefore(endDate)) || itemDate.isSame(endDate, 'day')
            }

            return isValid
          })
        }
      }

      // Apply category filter
      if (this.activeCategoryFilterIndex > 0 && this.categoryList && this.categoryFilterFunc) {
        target = target.filter(
          it => this.categoryFilterFunc(it, this.realCategoryList[this.activeCategoryFilterIndex]))
      }

      return target
    },
    realCategoryList () {
      return this.categoryList
        ? [
            {
              id: -1,
              name: this.$t('all')
            }, ...this.categoryList
          ]
        : []
    },
    filterDisplayChips: function () {
      const chips = []

      // Add chips for standard filters
      if (!this.showFilter) {
        const keys = Object.keys(this.filterItem)

        keys.filter(k => (
          !this.fixedFilter || !Object.keys(this.fixedFilter).includes(k)) &&
            [this.filterItem[k]].flat().length > 0)
          .forEach((k) => {
            const field = this.formDisc[k]

            if (field?.dataType === IKDataEntity.Types.Option) {
              const selectionGroup = groupBy(field.type?._selectItems ?? field.type.selectItems, field.type.itemValue)

              if (selectionGroup) {
                chips.push({
                  key: k,
                  name: field.text,
                  value: [this.filterItem[k]].flat().map(t => selectionGroup[t][0][field.type.itemText]).join(', ')
                })
              } else {
                chips.push({
                  key: k,
                  name: field.text,
                  value: [this.filterItem[k]].flat().join(',')
                })
              }
            } else if (field?.dataType === IKDataEntity.Types.Boolean) {
              chips.push({
                key: k,
                name: field.text,
                value: this.filterItem[k] ? 'Yes' : 'No'
              })
            } else if (field?.dataType === IKDataEntity.Types.String) {
              const operation = this.textFilterOperations[k]
              let opText = ''

              switch (operation) {
                case 'contains':
                  opText = this.$t('Contains')
                  break
                case 'startsWith':
                  opText = this.$t('StartsWith')
                  break
                case 'endsWith':
                  opText = this.$t('EndsWith')
                  break
                case 'equals':
                  opText = this.$t('Equals')
                  break
              }

              chips.push({
                key: k,
                name: `${field.text} (${opText})`,
                value: this.filterItem[k]
              })
            } else {
              chips.push({
                key: k,
                name: field.text,
                value: this.filterItem[k]
              })
            }
          })
      }

      // Add chips for numeric range filters
      for (const fieldKey in this.filterRanges) {
        const range = this.filterRanges[fieldKey]
        if ((range.min !== null && range.min !== '') || (range.max !== null && range.max !== '')) {
          const field = this.formDisc[fieldKey]
          let rangeText = ''

          if (range.min !== null && range.min !== '') {
            if (range.max !== null && range.max !== '') {
              rangeText = `${range.min} - ${range.max}`
            } else {
              rangeText = `≥ ${range.min}`
            }
          } else if (range.max !== null && range.max !== '') {
            rangeText = `≤ ${range.max}`
          }

          chips.push({
            key: `range_${fieldKey}`,
            name: field.text,
            value: rangeText
          })
        }
      }

      // Add chips for date range filters
      for (const fieldKey in this.filterDateRanges) {
        const dateRange = this.filterDateRanges[fieldKey]
        if (dateRange.start || dateRange.end) {
          const field = this.formDisc[fieldKey]
          let dateRangeText = ''

          if (dateRange.start) {
            if (dateRange.end) {
              dateRangeText = `${dateRange.start} - ${dateRange.end}`
            } else {
              dateRangeText = `${this.$t('From')} ${dateRange.start}`
            }
          } else if (dateRange.end) {
            dateRangeText = `${this.$t('Until')} ${dateRange.end}`
          }

          chips.push({
            key: `date_${fieldKey}`,
            name: field.text,
            value: dateRangeText
          })
        }
      }

      return chips
    },
    realHeaders () {
      // Use headerConfigUpdated to force re-evaluation when configuration changes
      // eslint-disable-next-line no-unused-vars
      const forceUpdate = this.headerConfigUpdated

      // Get custom header settings from localStorage if available
      const tableId = this.getTableId()
      const savedHeadersConfig = this.getSavedHeadersConfig(tableId)

      // Start with all available headers (including those from formFields)
      let availableHeaders = this.allAvailableHeaders

      // Add the action header if it exists in headers but not in availableHeaders
      const actionHeader = this.headers.find(h => h.value === 'action')
      if (actionHeader && !availableHeaders.some(h => h.value === 'action')) {
        availableHeaders.push(actionHeader)
      }

      // Apply custom visibility and order if available
      if (savedHeadersConfig) {
        // Create a map of header values to their configuration for faster lookups
        const headerConfigMap = Object.fromEntries(
          savedHeadersConfig.map(config => [config.value, config])
        )

        // Filter headers based on saved visibility settings
        availableHeaders = availableHeaders.filter(header => {
          // If header is in the saved config, use its visibility setting
          if (headerConfigMap[header.value]) {
            return headerConfigMap[header.value].visible !== false
          }
          // If header is not in the saved config, use its default header property
          return header.header !== false
        })

        // Sort headers based on saved order and ensure action header is first
        // First, prepare headers for sorting by adding a sortOrder property
        availableHeaders = availableHeaders.map(header => ({
          ...header,
          // Action header gets -1 to ensure it's always first
          // Other headers get their saved order or a high number as fallback
          sortOrder: header.value === 'action' ? -1 : (headerConfigMap[header.value]?.order ?? 999)
        }))

        // Then use lodash sortBy to sort by the sortOrder property
        availableHeaders = sortBy(availableHeaders, 'sortOrder')
      } else {
        // If no saved configuration, filter out headers with header=false
        availableHeaders = availableHeaders.filter(header => header.header !== false)

        // Map headers to add a sortOrder property
        availableHeaders = availableHeaders.map(header => ({
          ...header,
          // Action header gets -1 to ensure it's always first
          sortOrder: header.value === 'action' ? -1 : 0
        }))

        // Use lodash sortBy to sort by the sortOrder property
        availableHeaders = sortBy(availableHeaders, 'sortOrder')
      }

      // Return headers with translated text
      return availableHeaders.map(item => ({
        ...item,
        text: this.$i18n.t(item.text)
      }))
    },

    // Get all available headers (including those not currently displayed)
    allAvailableHeaders () {
      // Use headerConfigUpdated to force re-evaluation when configuration changes
      // eslint-disable-next-line no-unused-vars
      const forceUpdate = this.headerConfigUpdated

      // Combine headers and formFields, ensuring uniqueness by value
      return uniqBy([this.headers, this.formField].flat(), 'value')
        .filter(it => !it.onlyAction && !it.formOnly) // Exclude formOnly fields
        .map(item => ({
          ...item,
          text: this.$i18n.t(item.text)
        }))
    }
  },
  watch: {
    selectedItems (val) {
      this.$emit('selectedItems', val)
    },
    realFilter: {
      handler () {
        this.reload()
      }
    },
    requiredDateValue: {
      immediate: true,
      handler: function (val) {
        if (val?.length === 2 || val?.length === 1) {
          this.dates = val
        }
      }
    },
    activeCategoryFilterIndex (val) {
      this.$emit('index-update', val, this.realCategoryList)
    }
  },
  async mounted () {
    [this.headers, this.formField, this.defaultItem] = await IKDataEntity.parseField(this.model)
    for (const header of this.headers) {
      if ((typeof header.text === 'function')) {
        header.text = await IKUtils.safeCallFunction(this, header.text)
      }
    }
    this.formDisc = keyBy([this.formField, this.headers].flat(), 'value')

    if (this.useCustomAction || (this.useDefaultAction &&
        (this.useEditAction || this.useDeleteAction) && !this.onlyDisplay)) {
      this.headers.push({
        text: 'option',
        width: '60px',
        value: 'action',
        sortable: false
      })
    }

    if (this.fixedFilter) {
      this.defaultItem = Object.assign({}, this.defaultItem, this.fixedFilter)
    }

    // Initialize filter-related properties
    this.initializeFilterProperties()

    this.advancedItems = this.getAdvancedItems()
    this.slottedItems = this.getSlottedItems()
    this.editedItem = IKUtils.deepCopy(this.defaultItem)
    this.filterItem = IKUtils.deepCopy(this.fixedFilter ?? {})
    this.reload().catch(() => {
      this.loading = false
      this.items = []
    })
  },
  methods: {
    // Get a unique ID for the table based on entity name and model
    getTableId () {
      // Create a hash from entity name and model name
      const entityName = this.entityName || this.model.name()
      const modelName = this.model.constructor.name
      return `table_headers_${entityName}_${modelName}`
    },

    // Get saved header configuration from localStorage
    getSavedHeadersConfig (tableId) {
      try {
        const savedConfig = localStorage.getItem(tableId)
        return savedConfig ? JSON.parse(savedConfig) : null
      } catch (e) {
        console.error('Error loading saved header configuration:', e)
        return null
      }
    },

    // Save header configuration to localStorage
    saveHeadersConfig (headers) {
      try {
        const tableId = this.getTableId()
        localStorage.setItem(tableId, JSON.stringify(headers))
      } catch (e) {
        console.error('Error saving header configuration:', e)
      }
    },

    // Open header configuration dialog
    openHeaderConfig () {
      // Get table ID and saved configuration
      const tableId = this.getTableId()
      const savedConfig = this.getSavedHeadersConfig(tableId)

      // Initialize configHeaders with all available headers
      // Use Vue.set to update the entire array at once for reactivity
      this.configHeaders = sortBy(this.allAvailableHeaders.map((header, index) => {
        // Start with a copy of the header
        const configHeader = { ...header }

        // Set default values - start from 1 for regular headers
        configHeader.order = header.value === 'action' ? 0 : index
        // Set visibility based on header property - headers with header=false should be hidden by default
        configHeader.visible = header.header !== false

        // Apply saved configuration if available
        if (savedConfig) {
          const savedHeader = savedConfig.find(h => h.value === header.value)
          if (savedHeader) {
            configHeader.visible = savedHeader.visible !== false
            configHeader.order = savedHeader.order ?? configHeader.order
          }
        }

        return configHeader
      }), 'order')

      // Sort and normalize order values

      // Open the dialog
      this.headerConfigDialog = true
    },

    // Save header configuration and close dialog
    saveHeaderConfig () {
      // Ensure operation header is always first if present
      const operationHeader = this.configHeaders.find(h => h.value === 'action')
      if (operationHeader) {
        this.$set(operationHeader, 'order', 0)
      }

      // Sort and normalize order values before saving
      this.updateHeadersToIndex()

      // Save configuration to localStorage
      this.saveHeadersConfig(this.configHeaders)

      // Increment the counter to force realHeaders to re-evaluate
      this.headerConfigUpdated++

      // Close the dialog
      this.headerConfigDialog = false
    },

    // Reset header configuration to default values
    resetHeaderConfig () {
      // Initialize configHeaders with all available headers using default values
      const newConfigHeaders = this.allAvailableHeaders.map((header, index) => {
        // Start with a copy of the header
        const configHeader = { ...header }

        // Set default values - start from 1 for regular headers
        configHeader.order = header.value === 'action' ? 0 : index
        // Set visibility based on header property - headers with header=false should be hidden by default
        configHeader.visible = header.header !== false

        return configHeader
      })

      // Use Vue.set to update the entire array at once for reactivity
      this.configHeaders = newConfigHeaders

      // Sort and normalize order values
      this.updateHeadersToIndex()
    },
    updateHeadersToIndex () {
      // Find the action header
      const actionHeader = this.configHeaders.find(h => h.value === 'action')

      // If there's an action header, ensure it's always first by setting its order to -1
      if (actionHeader) {
        this.$set(actionHeader, 'order', -1)
      }

      // Sort headers by order value using lodash sortBy
      // This creates a new array sorted by the 'order' property
      const sortedHeaders = sortBy(this.configHeaders, 'order')

      // Replace the entire array to maintain reactivity
      this.configHeaders = sortedHeaders

      // Update order values to match the new positions
      this.configHeaders.forEach((header, index) => {
        this.$set(header, 'order', index)
      })
    },

    // Initialize filter properties for all filterable fields
    initializeFilterProperties () {
      const fields = uniqBy([this.formField, this.headers].flat(), 'value')

      // Initialize for numeric fields
      const numericFields = fields.filter(field =>
        field.dataType === IKDataEntity.Types.Float ||
          field.dataType === IKDataEntity.Types.Number
      )
      for (const field of numericFields) {
        this.$set(this.filterRanges, field.value, { min: null, max: null })
      }

      // Initialize for date fields
      const dateFields = fields.filter(field => field.dataType === IKDataEntity.Types.Date)
      for (const field of dateFields) {
        this.$set(this.filterDateRanges, field.value, { start: null, end: null })
        this.$set(this.dateMenus, field.value, { start: false, end: false })
      }

      // Initialize for text fields
      const textFields = fields.filter(field => field.dataType === IKDataEntity.Types.String)
      for (const field of textFields) {
        this.$set(this.textFilterOperations, field.value, 'contains')
      }
    },

    // Clear all filters
    clearFilters () {
      // Clear regular filters
      this.filterItem = IKUtils.deepCopy(this.fixedFilter ?? {})

      // Clear range filters
      for (const key in this.filterRanges) {
        this.$set(this.filterRanges, key, { min: null, max: null })
      }

      // Clear date filters
      for (const key in this.filterDateRanges) {
        this.$set(this.filterDateRanges, key, { start: null, end: null })
      }

      // Reset text operations to default
      for (const key in this.textFilterOperations) {
        this.$set(this.textFilterOperations, key, 'contains')
      }
    },

    // Apply filters and close the dialog
    applyFilters () {
      this.showFilter = false
    },

    // Remove a filter when its chip is clicked
    removeFilter (item) {
      const key = item.key

      // Handle range filters
      if (key.startsWith('range_')) {
        const fieldKey = key.replace('range_', '')
        if (this.filterRanges[fieldKey]) {
          this.$set(this.filterRanges, fieldKey, { min: null, max: null })
        }
      } else if (key.startsWith('date_')) { // Handle date filters
        const fieldKey = key.replace('date_', '')
        if (this.filterDateRanges[fieldKey]) {
          this.$set(this.filterDateRanges, fieldKey, { start: null, end: null })
        }
      } else {
        this.$delete(this.filterItem, key)
      }
    },

    compareFunction (a, b, operation) {
      if (operation === '>=') {
        return parseFloat(a) >= parseFloat(b)
      } else if (operation === '<=') {
        return parseFloat(a) <= parseFloat(b)
      } else if (operation === 'date') {
        return dayjs(a).format('YYYY-MM-DD') === dayjs(b).format('YYYY-MM-DD')
      } else {
        return a === b
      }
    },
    addNew (info) {
      this.$emit('add-new', info)
    },
    getItemClass (item) {
      const disabled = this.model.rowDisableFunction ? this.model.rowDisableFunction(item) : false
      const classString = (item?.id === this.lastClickItemId ? 'grey lighten-4' : '') +
          (this.onlyDisplay ? 'small' : '')
      const extraClass = this.model?.rowClass ? this.model.rowClass(item) : ''
      return classString + (disabled ? ' disabled' : '') + ' ' + extraClass
    },
    getNiceLabel,

    getAdvancedItems: function () {
      return this.headers
        .filter(item => [
          IKDataEntity.Types.Image, IKDataEntity.Types.Boolean,
          IKDataEntity.Types.Date,
          IKDataEntity.Types.Option, IKDataEntity.Types.Group,
          IKDataEntity.Types.Color, IKDataEntity.Types.Float
        ].includes(item.dataType) && !item.overwrite)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },
    getSlottedItems: function () {
      return this.headers
        .filter(item => item.overwrite)
        .map(item => {
          return {
            ...item,
            name: 'item.' + item.value
          }
        })
    },

    async toggleProperty (item, key) {
      if (!this.loading) {
        const _item = IKUtils.deepCopy(item)
        _item[key] = !_item[key]
        await this.updateItem(_item)
        IKUtils.toast(this.$i18n.t('EditSuccess'))
        await this.reload()
      }
    },

    async updateItem (item) {
      return await IKUtils.safeCallFunction(this.model, this.model.edit, item)
    },

    async deleteItem (item, promt = true) {
      if (promt) {
        const res = await IKUtils.showConfirmAsyn(
          this.$t('are_you_sure'),
          this.$t('you want to delete this item?')
        )
        if (res.isConfirmed) {
          await IKUtils.safeCallFunction(this.model, this.model.remove, this.useUUid ? item.uuid : item.id)
          IKUtils.toast(this.$t('delete_success'))
          this.reload()
        }
      } else {
        await IKUtils.safeCallFunction(this.model, this.model.remove, this.useUUid ? item.uuid : item.id)
        IKUtils.toast(this.$t('delete_success'))
        this.reload()
      }
    },
    async addItem () {
      if (this.useDefaultAction && this.useAddAction && !this.onlyDisplay) {
        this.editedIndex = -1
        const store = useBaseFormDialogStore()

        // Convert form fields to BaseForm schema
        const schemas = convertToBaseFormSchema(this.formField)

        // Open the dialog with default item
        const formData = await store.editItem({
          title: this.$t('new') + ' ' + '/' + this.$t(this.entityName),
          subtitle: '',
          schemas
        }, IKUtils.deepCopy(this.defaultItem))

        // Handle the result
        if (formData) {
          await this.handleFormSubmit(formData, -1)
        }
      }
    },

    async editItem (item) {
      if (this.useDefaultAction && this.useEditAction && !this.onlyDisplay) {
        const index = this.tableItem.indexOf(item)
        this.editedIndex = index
        this.lastClickItemId = item?.id

        const store = useBaseFormDialogStore()

        // Convert form fields to BaseForm schema
        const schemas = convertToBaseFormSchema(this.formField)

        // Open the dialog with the item to edit
        const formData = await store.editItem({
          title: this.$t('edit') + ' ' + '/' + this.$t(this.entityName),
          subtitle: '',
          schemas
        }, IKUtils.deepCopy(item))

        // Handle the result
        if (formData) {
          await this.handleFormSubmit(formData, index)
        }
      } else if (this.customOnRowClick) {
        this.customOnRowClick(item)
        this.lastClickItemId = item?.id
      }
    },

    // Find a language entity in an array of language entities
    findLangEntityInLangArr (lang, arr) {
      return arr.find(d => d.lang.toLowerCase() === lang.toLowerCase()) ?? null
    },

    // Copy values from one language entity to all other language entities in a group
    copyToAll (field, arr) {
      const [de, zh, en] = [
        this.findLangEntityInLangArr('DE', arr),
        this.findLangEntityInLangArr('ZH', arr),
        this.findLangEntityInLangArr('EN', arr)
      ]

      for (const fieldKey of field.childKey) {
        const [deContent, zhContent, enContent] = [de, zh, en].map(it => it[fieldKey])
        const target = deContent || zhContent || enContent

        const empty = [de, zh, en].filter(it => !it[fieldKey])
        for (const key of empty) {
          key[fieldKey] = target
        }
      }
    },

    async handleFormSubmit (formData, index) {
      try {
        // Process group fields
        const groupFields = this.formField.filter(f => f.dataType === IKDataEntity.Types.Group)
        for (const field of groupFields) {
          if (formData[field.value]) {
            this.copyToAll(field, formData[field.value])
          }
        }
        const imageFields = this.formField.filter(f => f.dataType === IKDataEntity.Types.Image)
        for (const field of imageFields) {
          if (formData[field.value] && (formData[field.value] instanceof Blob || formData[field.value] instanceof File)) {
            formData[field.value] = this.items[index][field.value]
            formData[field?.type?.fileStorage] = formData[field.value]
          }
        }
        if (index > -1) {
          // Edit existing item
          await this.updateItem(formData)
          IKUtils.toast(this.$t('EditSuccess'))
        } else {
          // Add new item
          await IKUtils.safeCallFunction(this.model, this.model.add, formData)
          IKUtils.toast(this.$t('addSuccess'))
          this.addNew(formData)
        }

        // Reload the data
        await this.reload()
      } catch (error) {
        console.error('Error saving item:', error)
        IKUtils.toast(this.$t('Error') + ': ' + error.message)
      }
    },

    async reload () {
      const model = this.model
      this.loading = true

      this.items = await IKUtils.safeCallFunction(model, model.getList, true, this.realFilter)
      this.$nextTick(() => {
        this.loading = false
        this.$emit('reloaded', this.items)
      })
    },

    pageUpdate () {
      this.$vuetify.goTo(0, {
        appOffset: true
      })
    }
  }
}
</script>
<style>

.paReset .v-toolbar__content {
  padding-right: 16px !important;
  padding-left: 16px !important;
}

.breakWord {
  word-break: break-all;
}

::-webkit-scrollbar {
  display: none;
}

.disabled {
  pointer-events: none;
  background: #f9f9f9;
  color: grey;
}

.v-simple-checkbox:not(.v-data-table__checkbox) {
  pointer-events: auto !important;
}

.small {
  font-size: xx-small !important;
  color: black;
}

/* Filter dialog and chip styles */
.filter-chip {
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 16px !important;
  max-width: 250px;
}

.filter-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}

.filter-field-container {
  padding: 16px;
  border-radius: 12px;
  background-color: rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  height: 100%;
}

.filter-field-container:hover {
  background-color: rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.filter-field-title {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 12px;
  color: rgba(0, 0, 0, 0.8);
  border-left: 3px solid #1976d2;
  padding-left: 12px;
}

.range-filter-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.range-filter-field {
  flex: 1;
  min-width: 0;
}

.range-filter-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  height: 56px;
}

/* Drag and drop styles */
.drag-handle {
  cursor: grab;
  transition: all 0.2s ease;
}

.drag-handle:hover {
  color: #1976d2 !important;
  transform: scale(1.2);
}

.action-header {
  background-color: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #ffc107;
}

.sortable-ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.sortable-drag {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: rotate(3deg);
  cursor: grabbing;
}

</style>
